/**
 * Bonus period dates. A period is identified by its LAST day: a Friday or Saturday for a weekly bonus, the last day of the
 * month for a monthly one. Same rules as the server (which is the one that enforces them).
 */
export type BonusFrequency = 'WEEKLY' | 'MONTHLY';

function parse(date: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? d : null;
}
const ymd = (d: Date) => d.toISOString().slice(0, 10);

/** A message when the date is not a valid last day for the frequency, otherwise null. */
export function periodEndProblem(frequency: BonusFrequency, periodEnd: string): string | null {
  const end = parse(periodEnd);
  if (!end) return 'Pick the last day of the bonus period.';
  if (frequency === 'WEEKLY') {
    const day = end.getUTCDay();
    return day === 5 || day === 6 ? null : 'A weekly bonus ends on a Friday or a Saturday.';
  }
  return new Date(end.getTime() + 86400000).getUTCDate() === 1 ? null : 'A monthly bonus ends on the last day of the month.';
}

/** The first and last day covered, for display. */
export function periodRange(frequency: BonusFrequency, periodEnd: string): { start: string; end: string } | null {
  const end = parse(periodEnd);
  if (!end || periodEndProblem(frequency, periodEnd)) return null;
  const start = frequency === 'WEEKLY' ? new Date(end.getTime() - 6 * 86400000) : new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  return { start: ymd(start), end: ymd(end) };
}

/** Shortcuts for the date picker. */
export function thisWeekFriday(today = new Date()): string {
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  return ymd(new Date(d.getTime() + ((5 - d.getUTCDay() + 7) % 7) * 86400000));
}
export function lastFridayOrToday(today = new Date()): string {
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  return ymd(new Date(d.getTime() - ((d.getUTCDay() - 5 + 7) % 7) * 86400000));
}
export function monthEnd(offset = 0, today = new Date()): string {
  return ymd(new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1 + offset, 0)));
}
